import type {
  AdminUser,
  CreateUserRequest,
  ListUsersRequest,
  ListUsersResponse,
} from './admin-types';
import type { UserRole } from './auth';

/**
 * Typed error thrown by the Admin API client for non-2xx responses
 * and network failures.
 */
export class AdminApiError extends Error {
  readonly status: number;
  readonly errorMessage: string;

  constructor(status: number, errorMessage: string) {
    super(errorMessage);
    this.name = 'AdminApiError';
    this.status = status;
    this.errorMessage = errorMessage;
  }
}

/** A function that returns the current JWT access token. */
export type AccessTokenGetter = () => string;

/**
 * Sends an authenticated request to the Admin API and returns the parsed
 * JSON response. Throws {@link AdminApiError} on non-2xx or network errors.
 */
async function request<T>(
  baseUrl: string,
  path: string,
  getAccessToken: AccessTokenGetter,
  options: RequestInit = {},
): Promise<T> {
  const url = `${baseUrl}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${getAccessToken()}`,
    ...(options.headers as Record<string, string> | undefined),
  };

  let response: Response;
  try {
    response = await fetch(url, { ...options, headers });
  } catch {
    throw new AdminApiError(0, 'Network error: unable to reach the server');
  }

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;
    try {
      const body = await response.json();
      if (body?.message) {
        errorMessage = body.message;
      }
    } catch {
      // response body wasn't JSON — keep the default message
    }
    throw new AdminApiError(response.status, errorMessage);
  }

  // Some endpoints (enable, disable, role update) return 204 No Content
  const text = await response.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/**
 * Creates a typed Admin API client bound to the given base URL and
 * token getter.
 */
export function createAdminApiClient(
  baseUrl: string,
  getAccessToken: AccessTokenGetter,
) {
  return {
    /** List users with optional pagination and email filter. */
    listUsers(params: ListUsersRequest = {}): Promise<ListUsersResponse> {
      const query = new URLSearchParams();
      if (params.limit != null) query.set('limit', String(params.limit));
      if (params.paginationToken) query.set('token', params.paginationToken);
      if (params.emailFilter) query.set('email', params.emailFilter);
      const qs = query.toString();
      return request<ListUsersResponse>(
        baseUrl,
        `/users${qs ? `?${qs}` : ''}`,
        getAccessToken,
      );
    },

    /** Get a single user by ID. */
    getUser(userId: string): Promise<AdminUser> {
      return request<AdminUser>(
        baseUrl,
        `/users/${encodeURIComponent(userId)}`,
        getAccessToken,
      );
    },

    /** Create a new Cognito user. */
    createUser(params: CreateUserRequest): Promise<AdminUser> {
      return request<AdminUser>(baseUrl, '/users', getAccessToken, {
        method: 'POST',
        body: JSON.stringify(params),
      });
    },

    /** Update a user's role. */
    updateUserRole(userId: string, role: UserRole): Promise<void> {
      return request<void>(
        baseUrl,
        `/users/${encodeURIComponent(userId)}/role`,
        getAccessToken,
        { method: 'PUT', body: JSON.stringify({ role }) },
      );
    },

    /** Enable a user account. */
    enableUser(userId: string): Promise<void> {
      return request<void>(
        baseUrl,
        `/users/${encodeURIComponent(userId)}/enable`,
        getAccessToken,
        { method: 'PUT' },
      );
    },

    /** Disable a user account. */
    disableUser(userId: string): Promise<void> {
      return request<void>(
        baseUrl,
        `/users/${encodeURIComponent(userId)}/disable`,
        getAccessToken,
        { method: 'PUT' },
      );
    },

    /** Delete a user permanently. */
    deleteUser(userId: string): Promise<void> {
      return request<void>(
        baseUrl,
        `/users/${encodeURIComponent(userId)}`,
        getAccessToken,
        { method: 'DELETE' },
      );
    },

    /** Update a user's email and/or phone number. */
    updateUserAttributes(
      userId: string,
      attrs: { email?: string; phoneNumber?: string },
    ): Promise<void> {
      return request<void>(
        baseUrl,
        `/users/${encodeURIComponent(userId)}/attributes`,
        getAccessToken,
        { method: 'PUT', body: JSON.stringify(attrs) },
      );
    },

    /** Reset a user's password with a new temporary password. */
    resetUserPassword(
      userId: string,
      temporaryPassword: string,
    ): Promise<void> {
      return request<void>(
        baseUrl,
        `/users/${encodeURIComponent(userId)}/reset-password`,
        getAccessToken,
        { method: 'POST', body: JSON.stringify({ temporaryPassword }) },
      );
    },
  };
}

/** Convenience type for the admin API client instance. */
export type AdminApiClient = ReturnType<typeof createAdminApiClient>;
